import React, { useEffect, useState } from 'react'
import DateOfBirthPicker from './DateOfBirthPicker'
import CountryCodePicker from './CountryCodePicker'
import {
  validateName,
  validateEmail,
  validatePhone,
  validateDateOfBirth,
  validateGender,
  normalizeNationalPhoneDigits,
  getNationalPhoneLimits,
} from '../utils/validation'
import { supabase } from '../config/supabase'

const RequiredMark = () => <span className="text-red-500"> *</span>

/**
 * Prompt Google / OAuth users to fill required profile fields.
 * Buttons: Complete Profile (save) | Later (dismiss).
 * No internal scrollbar — compact layout so both actions stay visible.
 */
const CompleteProfileModal = ({ user, profile = null, isOpen, onComplete, onLater }) => {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [countryCode, setCountryCode] = useState('+1')
  const [dateOfBirth, setDateOfBirth] = useState('')
  const [gender, setGender] = useState('')
  const [errors, setErrors] = useState({})
  const [formError, setFormError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setFirstName(profile?.first_name || user?.user_metadata?.given_name || '')
    setLastName(profile?.last_name || user?.user_metadata?.family_name || '')
    setEmail(profile?.email || user?.email || user?.user_metadata?.email || '')
    setPhone(profile?.mobile || '')
    setCountryCode(profile?.country_code || '+1')
    setDateOfBirth(profile?.date_of_birth || '')
    setGender(profile?.gender || '')
    setErrors({})
    setFormError('')
  }, [isOpen, profile, user])

  if (!isOpen || !user) return null

  const validate = () => {
    const next = {}
    const first = validateName(firstName, 'First name')
    if (!first.isValid) next.firstName = first.message
    const last = validateName(lastName, 'Last name')
    if (!last.isValid) next.lastName = last.message
    const mail = validateEmail(email)
    if (!mail.isValid) next.email = mail.message
    const mobile = validatePhone(phone, countryCode)
    if (!mobile.isValid) next.phone = mobile.message
    const dob = validateDateOfBirth(dateOfBirth, { minAge: 0, required: true })
    if (!dob.isValid) next.dateOfBirth = dob.message
    const g = validateGender(gender)
    if (!g.isValid) next.gender = g.message
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleCompleteBooking = async (e) => {
    e?.preventDefault?.()
    setFormError('')
    if (!validate()) return

    setIsSubmitting(true)
    try {
      const updateData = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim(),
        mobile: normalizeNationalPhoneDigits(phone, countryCode),
        country_code: countryCode,
        date_of_birth: dateOfBirth,
        gender: gender.trim(),
        updated_at: new Date().toISOString(),
      }

      const { error: updateError } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id)

      if (updateError) {
        setFormError(updateError.message || 'Failed to save profile. Please try again.')
        setIsSubmitting(false)
        return
      }

      await supabase.auth.updateUser({
        data: {
          first_name: updateData.first_name,
          last_name: updateData.last_name,
          date_of_birth: updateData.date_of_birth,
          gender: updateData.gender,
        },
      }).catch(() => {})

      onComplete?.(updateData)
    } catch (err) {
      console.error('Complete profile error:', err)
      setFormError('An unexpected error occurred. Please try again.')
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10060] p-3 sm:p-4">
      <div
        className="bg-white rounded-2xl max-w-lg w-full p-4 sm:p-5 shadow-xl overflow-visible"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center mb-3">
          <div className="mx-auto flex items-center justify-center h-11 w-11 rounded-full bg-primary/10 mb-2">
            <i className="fas fa-user-edit text-lg text-primary" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 mb-1">Complete Your Profile</h2>
          <p className="text-gray-600 text-xs sm:text-sm">
            Fill in the required fields before booking, or continue later.
          </p>
        </div>

        <form onSubmit={handleCompleteBooking} className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                First Name<RequiredMark />
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={`w-full px-2.5 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.firstName ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.firstName && <p className="text-red-500 text-[11px] mt-0.5">{errors.firstName}</p>}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-0.5">
                Last Name<RequiredMark />
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className={`w-full px-2.5 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.lastName ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.lastName && <p className="text-red-500 text-[11px] mt-0.5">{errors.lastName}</p>}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">
              Mobile Number<RequiredMark />
            </label>
            <div className="flex w-full items-stretch rounded-lg border border-gray-300 bg-white overflow-hidden divide-x divide-gray-300 focus-within:ring-2 focus-within:ring-primary focus-within:border-transparent">
              <CountryCodePicker
                selectedCountry={{ code: countryCode }}
                onCountrySelect={(country) => {
                  setCountryCode(country.code)
                  setPhone(normalizeNationalPhoneDigits(phone, country.code))
                }}
                embedded
              />
              <input
                type="tel"
                value={phone}
                onChange={(e) =>
                  setPhone(normalizeNationalPhoneDigits(e.target.value, countryCode))
                }
                className="flex-1 min-w-0 px-2.5 py-2 text-sm border-0 bg-transparent focus:ring-0 focus:outline-none"
                placeholder="Phone number"
                maxLength={getNationalPhoneLimits(countryCode).max}
              />
            </div>
            {errors.phone && <p className="text-red-500 text-[11px] mt-0.5">{errors.phone}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">
              Email<RequiredMark />
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={`w-full px-2.5 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.email ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.email && <p className="text-red-500 text-[11px] mt-0.5">{errors.email}</p>}
          </div>

          <DateOfBirthPicker
            id="complete-profile-dob"
            label="Date of Birth"
            value={dateOfBirth}
            onChange={(next) => {
              setDateOfBirth(next)
              setErrors((prev) => {
                const n = { ...prev }
                delete n.dateOfBirth
                return n
              })
            }}
            error={errors.dateOfBirth}
            required
            compact
          />

          <div>
            <label className="block text-xs font-medium text-gray-700 mb-0.5">
              Gender<RequiredMark />
            </label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className={`w-full px-2.5 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                errors.gender ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Select gender</option>
              <option value="Female">Female</option>
              <option value="Male">Male</option>
              <option value="Other">Other</option>
              <option value="Prefer not to say">Prefer not to say</option>
            </select>
            {errors.gender && <p className="text-red-500 text-[11px] mt-0.5">{errors.gender}</p>}
          </div>

          {formError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-xs text-red-700">
              {formError}
            </div>
          )}

          <div className="pt-1 space-y-2">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-secondary text-white py-2.5 rounded-lg hover:bg-gray-800 transition font-semibold text-sm disabled:opacity-50"
            >
              {isSubmitting ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="fas fa-spinner fa-spin" />
                  Saving…
                </span>
              ) : (
                'Complete Profile'
              )}
            </button>

            <button
              type="button"
              onClick={() => onLater?.()}
              disabled={isSubmitting}
              className="w-full py-2.5 rounded-lg border border-gray-300 bg-white text-gray-800 font-semibold text-sm hover:bg-gray-50 transition disabled:opacity-50"
            >
              Later
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CompleteProfileModal
